import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EzLogo, AboutUsLink } from "@/pages/LandingPage";
import mixpanel from "@/mixpanel";
import {
  Phone, Heart, Users, DollarSign, MapPin, Clock,
  Briefcase, TrendingUp, ArrowRight, CheckCircle2, Star, Quote,
  ChevronLeft, ChevronRight, Shield
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const API = process.env.REACT_APP_BACKEND_URL;

const CLIENT_TESTIMONIALS = [
  {
    quote: "My guide helped me understand my Medicare options in a way no one else could. She didn't just read me a list — she actually listened and found solutions I didn't know existed.",
    name: "Judith",
    age: 74,
    location: "Tampa, FL",
    detail: "Saved $320/mo on prescriptions",
  },
  {
    quote: "After my husband passed, I felt completely lost dealing with insurance and doctors. My guide called me every week — not because she had to, but because she genuinely cared.",
    name: "Patricia",
    age: 81,
    location: "Phoenix, AZ",
    detail: "Connected to 4 new support services",
  },
  {
    quote: "I was paying for three supplements that actually conflicted with my medications. My guide caught that in our very first call and helped me talk to my doctor about it.",
    name: "Margaret",
    age: 77,
    location: "Chicago, IL",
    detail: "Medication plan fully reviewed",
  },
  {
    quote: "I'm not great with computers, and the healthcare system feels like it was designed for people half my age. My guide walks me through everything on the phone — appointments, claims, even finding a new dentist.",
    name: "Helen",
    age: 83,
    location: "Boston, MA",
    detail: "12 months with her guide",
  },
  {
    quote: "My daughter lives across the country and worries about me constantly. Since I started working with my guide, my daughter says she finally sleeps at night. That alone is worth everything.",
    name: "Dorothy",
    age: 79,
    location: "Denver, CO",
    detail: "Family peace of mind",
  },
];

export default function SalesLandingPage() {
  const navigate = useNavigate();
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [smsFirstName, setSmsFirstName] = useState("");

  useEffect(() => { mixpanel.track("salesPageViewed"); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("user");
    if (!userId) return;
    fetch(`${API}/api/indeed-prefill/${userId}`)
      .then(r => r.json())
      .then(data => {
        if (data.found && data.name) {
          const first = data.name.trim().split(" ")[0];
          setSmsFirstName(first.charAt(0).toUpperCase() + first.slice(1).toLowerCase());
        }
        if (data.found && data.email) {
          mixpanel.identify(data.email);
          mixpanel.people.set({ $email: data.email, $name: data.name || "", source: "indeed", job_type: "sales" });
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!autoRotate) return;
    const timer = setInterval(() => {
      setTestimonialIndex(prev => (prev + 1) % CLIENT_TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [autoRotate]);

  const goToTestimonial = (dir) => {
    setAutoRotate(false);
    setTestimonialIndex(prev => {
      if (dir === "next") return (prev + 1) % CLIENT_TESTIMONIALS.length;
      return (prev - 1 + CLIENT_TESTIMONIALS.length) % CLIENT_TESTIMONIALS.length;
    });
    setTimeout(() => setAutoRotate(true), 10000);
  };

  const handleFirstQuestion = (answer) => {
    mixpanel.track("salesApplicationStarted", { source: "hero_question", answer: answer ? "yes" : "no" });
    const params = window.location.search;
    navigate(`/sales/apply${params}`, { state: { can_work_full_time: answer } });
  };

  const responsibilities = [
    { icon: <Phone className="w-5 h-5" />, title: "Make outbound calls to seniors", desc: "Connect with older adults who need help navigating healthcare options — Medicare, insurance, and care services." },
    { icon: <Heart className="w-5 h-5" />, title: "Build genuine relationships", desc: "Listen to seniors' needs and guide them toward the right solutions. Empathy and patience are your superpowers." },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Hit & exceed targets", desc: "Use a proven playbook to consistently hit monthly goals. Top performers earn well above base with uncapped commissions." },
    { icon: <Users className="w-5 h-5" />, title: "Collaborate with a growing team", desc: "Work alongside a supportive remote team with weekly coaching, clear KPIs, and real advancement opportunities." },
  ];

  const offers = [
    "Competitive base salary plus uncapped commission — our top reps earn over $110K and even our newest reps make $75K+",
    "Full health insurance and benefits from day one",
    "100% remote — work from anywhere in the world",
    "Daytime hours with flexibility built in",
    "Work directly with founders who scaled a healthcare business to $3.4B+",
  ];

  const idealFor = [
    "Insurance sales reps", "Telemarketers & cold callers", "B2C phone sales pros",
    "Appointment setters", "Medicare sales agents", "Anyone with phone sales grit"
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="sales-landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="sales-landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-5 sm:gap-7">
            <EzLogo size="h-9" />
            <AboutUsLink />
          </div>
          <Button
            data-testid="header-apply-btn"
            onClick={() => navigate(`/sales/apply${window.location.search}`)}
            className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-6 font-semibold shadow-lg shadow-blue-100"
          >
            {smsFirstName ? "Finish Application" : "Apply in 2 Minutes"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="sales-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#EDF5FB] to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div {...fadeUp} className="space-y-8">
              {smsFirstName ? (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ez-teal font-heading leading-[1.1]">
                    Welcome <span className="text-ez-blue">{smsFirstName}</span>
                  </h1>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl font-body">
                    Based on your background, you could be a strong fit
                    for our Sales role — helping seniors
                    navigate healthcare while earning great commission.
                  </p>
                  <div>
                    <p className="text-base font-semibold text-ez-teal font-heading mb-4">Take 2 minutes to finish your application now.</p>
                    <button
                      data-testid="hero-finish-btn"
                      onClick={() => navigate(`/sales/apply${window.location.search}`)}
                      className="px-10 py-4 rounded-full text-base font-semibold font-heading bg-ez-blue/80 text-white hover:bg-ez-blue shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                    >
                      Finish My Application
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ez-teal font-heading leading-[1.1]" data-testid="hero-title">
                    Join Our Remote <span className="text-ez-blue">Sales Team</span>
                  </h1>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl font-body">
                    Make $110k+ from home with steady base pay, uncapped commission, and full benefits. Join a rewarding outbound sales role helping seniors get the support they need in a complicated healthcare system.
                  </p>
                  <div>
                    <p className="text-base font-semibold text-ez-teal font-heading mb-4">Are you available to work full-time (40 hours/week)?</p>
                    <div className="flex gap-3 max-w-xs">
                      <button
                        data-testid="hero-yes-btn"
                        onClick={() => handleFirstQuestion(true)}
                        className="flex-1 py-3.5 rounded-full text-base font-semibold font-heading bg-ez-blue text-white hover:bg-[#5A9BCF] shadow-lg shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                      >
                        Yes
                      </button>
                      <button
                        data-testid="hero-no-btn"
                        onClick={() => handleFirstQuestion(false)}
                        className="flex-1 py-3.5 rounded-full text-base font-semibold font-heading bg-white text-ez-teal border-2 border-slate-200 hover:border-ez-blue/50 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                      >
                        No
                      </button>
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center gap-6 pt-2 text-sm text-slate-500 font-body">
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> 100% Remote</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Full-Time</span>
                <span className="flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> Base + Commission</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-100 aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1713947506536-2edbac14d3bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Woman making sales calls from her home office"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                    <Star className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-ez-teal text-sm font-heading">Founded by Noom veterans</p>
                    <p className="text-xs text-slate-500 font-body">Scaled to $3.4B+ in healthcare</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Carousel */}
      <section className="py-20 lg:py-24 bg-white" data-testid="client-testimonial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Real Stories</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">The Impact You'll Make</h2>
            <p className="text-base text-slate-500 font-body mt-3">We've helped <span className="font-semibold text-ez-teal">2,000+ seniors</span> all across the US — rated <a href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638/customer-reviews" target="_blank" rel="noopener noreferrer" className="font-semibold text-ez-teal hover:text-ez-blue transition-colors duration-200">4.97 on the BBB</a>.</p>
          </motion.div>

          <div className="max-w-4xl mx-auto relative" data-testid="testimonial-carousel">
            <div className="bg-ez-surface rounded-3xl p-8 md:p-12 border border-slate-100 relative overflow-hidden min-h-[280px]">
              <Quote className="w-16 h-16 text-ez-blue/10 absolute top-6 left-6" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={testimonialIndex}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35 }}
                  className="relative"
                >
                  <p className="text-lg md:text-xl text-ez-teal font-heading leading-relaxed mb-8" data-testid="testimonial-quote">
                    {CLIENT_TESTIMONIALS[testimonialIndex].quote}
                  </p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-12 h-12 rounded-full bg-ez-blue/10 flex items-center justify-center">
                      <span className="text-ez-blue font-bold text-lg font-heading">
                        {CLIENT_TESTIMONIALS[testimonialIndex].name[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-ez-teal font-heading" data-testid="testimonial-name">
                        {CLIENT_TESTIMONIALS[testimonialIndex].name}, {CLIENT_TESTIMONIALS[testimonialIndex].age}
                      </p>
                      <p className="text-sm text-slate-500 font-body">
                        Ez Health Client — {CLIENT_TESTIMONIALS[testimonialIndex].location}
                      </p>
                    </div>
                    <div className="ml-auto hidden sm:block">
                      <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-semibold font-body" data-testid="testimonial-detail">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {CLIENT_TESTIMONIALS[testimonialIndex].detail}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                data-testid="testimonial-prev-btn"
                onClick={() => goToTestimonial("prev")}
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-ez-blue hover:border-ez-blue transition-colors duration-200"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2" data-testid="testimonial-dots">
                {CLIENT_TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setAutoRotate(false);
                      setTestimonialIndex(i);
                      setTimeout(() => setAutoRotate(true), 10000);
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === testimonialIndex ? "w-8 bg-ez-blue" : "w-2 bg-slate-300 hover:bg-slate-400"
                    }`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next-btn"
                onClick={() => goToTestimonial("next")}
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-ez-blue hover:border-ez-blue transition-colors duration-200"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="py-20 lg:py-28 bg-white" data-testid="responsibilities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">The Role</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">What You'll Do</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            {responsibilities.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="flex gap-5 p-8 bg-ez-surface rounded-2xl border border-slate-100 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-ez-blue/10 flex items-center justify-center text-ez-blue">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-ez-teal text-lg mb-2 font-heading">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed font-body text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 lg:py-28 bg-ez-surface" data-testid="offers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Benefits</p>
              <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-8">What Ez Health Offers You</h2>
              <div className="space-y-4">
                {offers.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08, duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-body">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1758522484779-b852d5582d52?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Woman talking on phone while working on laptop at home"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-20 lg:py-28 bg-white" data-testid="ideal-candidate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">Who Fits</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-4">This Role is Perfect For</h2>
            <p className="text-base md:text-lg text-slate-600 mb-12 max-w-2xl mx-auto font-body">
              We're looking for driven sales professionals who love connecting with people over the phone and want to make a real impact in seniors' lives.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {idealFor.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.3 }}
                className="bg-ez-surface rounded-xl px-5 py-4 border border-slate-100 hover:border-ez-blue/30 transition-colors duration-200"
              >
                <div className="flex items-center justify-center gap-2">
                  <Briefcase className="w-4 h-4 text-ez-blue" />
                  <span className="font-medium text-ez-teal text-sm font-body">{item}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rep Testimonials */}
      <section className="py-20 lg:py-24 bg-ez-surface" data-testid="rep-testimonial-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-sm font-semibold text-ez-blue uppercase tracking-wider mb-3 font-body">From Our Team</p>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight">Hear From People Like You</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                quote: "I love this job because of the flexibility and I'm able to be present in my kids' lives working from home. I have a great boss who's understanding and listens to my concerns. I finally feel like I found my home where I'm being heard and appreciated. The money is fabulous as well which is huge for me! I love the fact that I'm helping seniors stay home and independent and making a difference in the quality of their lives.",
                name: "Amy D.",
                prev: "Sales Concierge",
                months: "Ez Health team member",
              },
              {
                quote: "I'm passionate about helping seniors feel empowered when it comes to their benefits. Many don't realize what they qualify for, and I take pride in uncovering those opportunities and making the process seamless. Knowing I've helped someone access care, save money, or simply feel less stressed is what makes this work so meaningful!",
                name: "Meredith B.",
                prev: "Sales Concierge",
                months: "Ez Health team member",
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.4 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <Quote className="w-8 h-8 text-ez-peach/40 mb-4" />
                <p className="text-slate-700 font-body leading-relaxed mb-6">{t.quote}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-ez-blue/10 flex items-center justify-center">
                    <span className="text-ez-blue font-bold text-sm font-heading">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-ez-teal text-sm font-heading">{t.name}</p>
                    <p className="text-xs text-slate-500 font-body">{t.prev} · {t.months}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Details Strip */}
      <section className="py-12 bg-ez-teal" data-testid="job-details-strip">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { label: "Location", value: "100% Remote" },
            { label: "Compensation", value: "Base + Commission" },
            { label: "Hours", value: "40 hrs/week" },
            { label: "Benefits", value: "Full Health Insurance" },
          ].map((item, i) => (
            <div key={i}>
              <p className="text-ez-peach text-xs font-semibold uppercase tracking-wider mb-1 font-body">{item.label}</p>
              <p className="text-white font-semibold text-lg font-heading">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-6">
              Ready to Join the Team?
            </h2>
            <p className="text-base md:text-lg text-slate-600 mb-4 font-body">
              It takes just 2 minutes to apply. No cover letter needed — just a few quick questions and your resume.
            </p>
            <p className="text-sm text-slate-500 font-body italic mb-10">
              "I love the fact that I'm helping seniors stay home and independent and making a difference in the quality of their lives." — Amy D., Sales Concierge
            </p>
            <Button
              data-testid="cta-apply-btn"
              onClick={() => navigate(`/sales/apply${window.location.search}`)}
              className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-10 py-6 text-lg font-semibold shadow-xl shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
              size="lg"
            >
              Start Your Application <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-ez-surface border-t border-slate-100" data-testid="sales-landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <EzLogo size="h-6" />
              <p className="text-sm text-slate-500 font-body">Making healthcare easier for seniors, one call at a time.</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-body">
              <a href="https://www.medicare.gov/care-compare/details/group-practice/7214469816?id=ez-recruitment&city=North%20Miami&state=FL&zipcode=33181" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">Medicare</a>
              <a href="https://www.bbb.org/us/fl/miami/profile/health-consultants/ez-health-medical-group-pa-0633-92044638" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">BBB</a>
              <a href="https://www.linkedin.com/company/ez-health-inc" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">LinkedIn</a>
              <a href="https://www.facebook.com/people/EZ-Health/61565359091888/" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">Facebook</a>
              <span className="text-slate-300">|</span>
              <a href="/privacy" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">Privacy Policy</a>
              <a href="/terms" className="text-slate-500 hover:text-ez-blue transition-colors duration-200">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
